import React from 'react'

import { Rule } from '../interface'

type FieldState = {
  value: any
  errors?: string[]
  touched: boolean
}

export type FormStoreInstance = {
  getFieldDecorator: (
    name: string,
    options?: { rules?: Rule[] }
  ) => (node: React.ReactElement) => React.ReactElement
  getFieldValue: (name: string) => any
  getFieldsValue: () => Record<string, any>
  setFieldsValue: (values: Record<string, any>) => void
  setFields: (
    fieldMap: Record<string, { value?: any; errors?: any; touched?: boolean }>
  ) => void
  validateFields: (fieldNames?: string[]) => Promise<void>
  getFieldError: (name: string) => string[] | false
  getFieldsError: () => Record<string, string[] | undefined>
  isFieldTouched: (name: string) => boolean
  resetFields: (fieldNames?: string[]) => void
}

const runRules = async (
  name: string,
  value: any,
  rules: Rule[]
): Promise<string[]> => {
  const errors: string[] = []
  for (const rule of rules) {
    if (rule.required) {
      if (value === undefined || value === null || value === '') {
        errors.push(rule.message || `${name} is required`)
        continue
      }
    }
    if (rule.type === 'number') {
      const num = Number(value)
      if (!isNaN(num)) {
        if (rule.min !== undefined && num < rule.min) {
          errors.push(rule.message || `Value must be at least ${rule.min}`)
          continue
        }
        if (rule.max !== undefined && num > rule.max) {
          errors.push(rule.message || `Value must be at most ${rule.max}`)
          continue
        }
      }
    }
    if (rule.validator) {
      try {
        await rule.validator(rule, value)
      } catch (e: any) {
        errors.push(e?.message || String(e) || 'Validation failed')
      }
    }
  }
  return errors
}

export const createFormStore = (forceUpdate: () => void): FormStoreInstance => {
  const fieldStates: Record<string, FieldState> = {}
  const fieldRules: Record<string, Rule[]> = {}

  const store: FormStoreInstance = {
    getFieldDecorator: (name, options) => {
      if (!fieldStates[name]) {
        fieldStates[name] = { value: undefined, touched: false }
      }
      if (options?.rules) {
        fieldRules[name] = options.rules
      }

      return (node: React.ReactElement) => {
        const originalOnChange = node.props.onChange
        const storeValue = fieldStates[name]?.value
        const valueProps = storeValue !== undefined ? { value: storeValue } : {}

        return React.cloneElement(node, {
          ...valueProps,
          onChange: async (...args: any[]) => {
            const e = args[0]
            const raw = e?.target !== undefined ? e.target.value : e
            const errors = await runRules(name, raw, fieldRules[name] || [])
            fieldStates[name] = {
              value: raw,
              touched: true,
              errors: errors.length ? errors : undefined,
            }
            forceUpdate()
            originalOnChange?.(...args)
          },
        } as any)
      }
    },

    getFieldValue: (name) => fieldStates[name]?.value,

    getFieldsValue: () => {
      const result: Record<string, any> = {}
      Object.keys(fieldStates).forEach((k) => {
        result[k] = fieldStates[k].value
      })
      return result
    },

    setFieldsValue: (values) => {
      Object.entries(values).forEach(([k, v]) => {
        fieldStates[k] = {
          ...(fieldStates[k] ?? { touched: false }),
          value: v,
        }
      })
      forceUpdate()
    },

    setFields: (fieldMap) => {
      Object.entries(fieldMap).forEach(([k, state]) => {
        const existing = fieldStates[k] ?? { value: undefined, touched: false }
        const rawErrors = state.errors
        const errors = rawErrors
          ? (Array.isArray(rawErrors)
              ? rawErrors.map((e: any) =>
                  typeof e === 'string' ? e : e?.message || String(e)
                )
              : [String(rawErrors)]
            ).filter(Boolean)
          : undefined
        fieldStates[k] = {
          value: 'value' in state ? state.value : existing.value,
          errors: errors?.length ? errors : undefined,
          touched: state.touched ?? existing.touched,
        }
      })
      forceUpdate()
    },

    validateFields: async (fieldNames) => {
      const names = fieldNames || Object.keys(fieldStates)
      const errorMap: Record<
        string,
        { errors: Array<{ message: string; field: string }> }
      > = {}

      for (const name of names) {
        const value = fieldStates[name]?.value
        const rules = fieldRules[name] || []
        const errors = await runRules(name, value, rules)
        if (errors.length > 0) {
          fieldStates[name] = {
            ...(fieldStates[name] ?? { touched: false }),
            errors,
          }
          errorMap[name] = {
            errors: errors.map((msg) => ({ message: msg, field: name })),
          }
        }
      }

      if (Object.keys(errorMap).length > 0) {
        forceUpdate()
        const err: any = new Error('Validation failed')
        err.errors = errorMap
        throw err
      }
    },

    getFieldError: (name) => {
      const errors = fieldStates[name]?.errors
      return errors?.length ? errors : false
    },

    getFieldsError: () => {
      const result: Record<string, string[] | undefined> = {}
      Object.keys(fieldStates).forEach((k) => {
        result[k] = fieldStates[k].errors
      })
      return result
    },

    isFieldTouched: (name) => fieldStates[name]?.touched ?? false,

    resetFields: (fieldNames) => {
      const names = fieldNames || Object.keys(fieldStates)
      names.forEach((name) => {
        if (fieldStates[name]) {
          fieldStates[name] = {
            value: undefined,
            touched: false,
            errors: undefined,
          }
        }
      })
      forceUpdate()
    },
  }

  return store
}
